import React, { useState, useRef, useEffect } from 'react';
import { FaPaperclip, FaArrowLeft, FaSave, FaPaperPlane, FaGlobe, FaInfoCircle } from 'react-icons/fa';
import { sendSecureMessageClient, saveDraft } from '../../services/messagingService';
import { sendDirectMessage, getAvailableHispConnections, createAttachmentFromFile } from '../../services/directMessagingService';
import { useAuth } from '../../context/AuthContext';
import { useUserData } from '../../hooks/useUserData';
import AddressBook from './AddressBook';

const priorities = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Urgent', label: 'Urgent' },
];

// Check if a recipient is an external Direct address
const isDirectAddress = (recipient) => {
  if (!recipient) return false;
  const trimmed = recipient.trim().toLowerCase();
  // Direct addresses typically contain @ and look like email addresses
  // Common patterns: *.direct.*, *@direct.*, healthcare Direct addresses
  return trimmed.includes('@') && (
    trimmed.includes('.direct.') ||
    trimmed.includes('@direct.') ||
    trimmed.endsWith('.direct') ||
    // Generic external email detection - any email with a domain
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
  );
};

const ComposeMessageModal = ({ onClose, replyTo = null, forwardFrom = null }) => {
  const { user } = useAuth();
  const { defaultFacility } = useUserData();
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddressBook, setShowAddressBook] = useState(false);
  const contentEditableRef = useRef(null);
  
  // Direct messaging state
  const [hispConnections, setHispConnections] = useState([]);
  const [selectedHispConnection, setSelectedHispConnection] = useState('');
  const [isExternalMessage, setIsExternalMessage] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  
  // Load HISP connections on mount
  useEffect(() => {
    const loadConnections = async () => {
      setLoadingConnections(true);
      try {
        const connections = await getAvailableHispConnections(defaultFacility?.id);
        setHispConnections(connections);
        if (connections.length === 1) {
          setSelectedHispConnection(connections[0].id);
        }
      } catch (err) {
        console.error('Error loading HISP connections:', err);
      } finally {
        setLoadingConnections(false);
      }
    };
    loadConnections();
  }, [defaultFacility?.id]);
  
  // Detect if any recipient (To, CC, or BCC) is an external Direct address
  useEffect(() => {
    const allRecipients = [to, cc, bcc]
      .join(',')
      .split(/[,;]/)
      .map(r => r.trim())
      .filter(Boolean);
    const hasExternal = allRecipients.some(r => isDirectAddress(r));
    setIsExternalMessage(hasExternal);
  }, [to, cc, bcc]);

  // Initialize contentEditable with body content
  useEffect(() => {
    if (contentEditableRef.current && body !== contentEditableRef.current.innerHTML) {
      contentEditableRef.current.innerHTML = body;
    }
  }, [body]);

  // Initialize form for reply or forward
  useEffect(() => {
    if (replyTo) {
      if (replyTo.type === 'draft') {
        // Editing a draft: pre-fill with draft data
        setTo(replyTo.recipients?.join(', ') || '');
        setSubject(replyTo.subject || '');
        setBody(replyTo.content || '');
        setPriority(replyTo.priority || 'Normal');
      } else {
        // Reply: set recipient to original sender, add "Re:" prefix to subject, include original message
        setTo(replyTo.senderName || replyTo.sentBy || '');
        setSubject(replyTo.subject ? `Re: ${replyTo.subject}` : 'Re: ');
        setBody(`<br><br><hr><br><strong>From:</strong> ${replyTo.senderName || 'Unknown'}<br><strong>Date:</strong> ${replyTo.sentAt ? new Date(replyTo.sentAt.seconds * 1000).toLocaleString() : ''}<br><strong>Subject:</strong> ${replyTo.subject || ''}<br><br>${replyTo.content || ''}`);
      }
    } else if (forwardFrom) {
      // Forward: clear recipients, add "Fwd:" prefix to subject, include original message
      setTo('');
      setSubject(forwardFrom.subject ? `Fwd: ${forwardFrom.subject}` : 'Fwd: ');
      setBody(`<br><br><hr><br><strong>From:</strong> ${forwardFrom.senderName || 'Unknown'}<br><strong>Date:</strong> ${forwardFrom.sentAt ? new Date(forwardFrom.sentAt.seconds * 1000).toLocaleString() : ''}<br><strong>Subject:</strong> ${forwardFrom.subject || ''}<br><br>${forwardFrom.content || ''}`);
    }
  }, [replyTo, forwardFrom]);

  const handleAttach = (e) => {
    setAttachments([...attachments, ...Array.from(e.target.files)]);
  };

  // Simple rich text toolbar actions (bold, italic, underline, lists)
  const handleFormat = (cmd) => {
    document.execCommand(cmd, false, null);
  };

  // Parse recipients from comma/semicolon separated fields
  const parseRecipients = (...fields) => {
    return fields
      .join(',')
      .split(/[,;]/)
      .map(s => s.trim())
      .filter(Boolean);
  };

  const handleSend = async () => {
    setError('');
    if (!user) {
      setError('You must be logged in to send messages.');
      return;
    }
    if (!to && !cc && !bcc) {
      setError('Please enter at least one recipient.');
      return;
    }
    if (!subject || !body) {
      setError('Subject and message body are required.');
      return;
    }
    
    // Check if sending to external Direct address
    if (isExternalMessage) {
      if (hispConnections.length === 0) {
        setError('No HISP connection available. Please contact your administrator to configure Direct messaging.');
        return;
      }
      if (!selectedHispConnection) {
        setError('Please select a HISP connection for external Direct messaging.');
        return;
      }
    }
    
    setLoading(true);
    try {
      const recipients = parseRecipients(to, cc, bcc);
      
      if (isExternalMessage) {
        // Send via Direct protocol (HISP)
        // Convert attachments to base64 if any
        const directAttachments = await Promise.all(
          attachments.map(file => createAttachmentFromFile(file))
        );
        
        // Send to each external recipient
        const externalRecipients = recipients.filter(r => isDirectAddress(r));
        const internalRecipients = recipients.filter(r => !isDirectAddress(r));
        
        // Send external messages via HISP
        for (const recipient of externalRecipients) {
          const result = await sendDirectMessage({
            hispConnectionId: selectedHispConnection,
            facilityId: defaultFacility?.id,
            to: recipient,
            subject,
            body,
            attachments: directAttachments.length > 0 ? directAttachments : undefined,
            metadata: { priority, senderName: user.displayName || user.email }
          });
          
          if (!result.success) {
            throw new Error(result.error || `Failed to send to ${recipient}`);
          }
        }
        
        // If there are also internal recipients, send internal message too
        if (internalRecipients.length > 0) {
          await sendSecureMessageClient({
            recipients: internalRecipients,
            subject,
            message: body,
            priority,
            senderName: user.displayName || user.email || 'Unknown',
          });
        }
      } else {
        // Send internal secure message
        await sendSecureMessageClient({
          recipients,
          subject,
          message: body,
          priority,
          senderName: user.displayName || user.email || 'Unknown',
        });
      }
      
      setLoading(false);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send message.');
      setLoading(false);
    }
  };

  // Handle selecting a contact from the address book
  const handleSelectContact = (contact) => {
    // Add contact name/email to the To field (comma separated)
    setTo(prev => prev ? prev + ', ' + contact.name : contact.name);
    setShowAddressBook(false);
  };

  // Handle saving draft
  const handleSaveDraft = async () => {
    console.log('=== Save Draft Debug ===');
    console.log('User object:', user);
    console.log('User UID:', user?.uid);
    console.log('User email:', user?.email);
    console.log('User displayName:', user?.displayName);
    console.log('Auth state:', !!user);
    
    if (!user) {
      setError('You must be logged in to save drafts.');
      return;
    }
    
    if (!user.uid) {
      setError('User ID is missing. Please log out and log back in.');
      return;
    }
    
    console.log('Current user:', user);
    console.log('Attempting to save draft...');
    
    setLoading(true);
    setError(''); // Clear any previous errors
    
    try {
      const recipients = parseRecipients(to, cc, bcc);
      console.log('Parsed recipients:', recipients);
      
      const draftData = {
        recipients,
        subject,
        message: body,
        priority,
        senderId: user.uid,
        senderName: user.displayName || user.email || 'Unknown',
      };
      
      console.log('Draft data to save:', draftData);
      console.log('Sender ID being used:', draftData.senderId);
      console.log('Current auth UID:', user.uid);
      console.log('Are they equal?', draftData.senderId === user.uid);
      
      await saveDraft(draftData);
      console.log('Draft saved successfully');
      setLoading(false);
      onClose();
    } catch (err) {
      console.error('Error in handleSaveDraft:', err);
      setError(err.message || 'Failed to save draft.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 8, width: 500, maxWidth: '98vw',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)', padding: 0, overflow: 'hidden',
      }}>
        {/* Top Bar */}
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0', padding: '12px 20px', background: '#fafbfc' }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, marginRight: 12, cursor: 'pointer' }}><FaArrowLeft /></button>
          <span style={{ fontWeight: 600, fontSize: 16, flex: 1 }}>
            {replyTo?.type === 'draft' ? 'Edit Draft' : replyTo ? 'Reply to Message' : forwardFrom ? 'Forward Message' : 'Compose Message'}
          </span>
          <button 
            style={{ background: 'none', border: 'none', color: '#888', fontWeight: 500, marginRight: 12, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: loading ? 0.6 : 1 }} 
            onClick={handleSaveDraft}
            disabled={loading}
          >
            <FaSave style={{ marginRight: 4 }} />{loading ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={handleSend}
            disabled={loading}
          >
            <FaPaperPlane style={{ marginRight: 6 }} />{loading ? 'Sending...' : 'Send'}
          </button>
        </div>
        {/* Form */}
        <div style={{ padding: 16 }}>
          {error && <div style={{ color: 'red', marginBottom: 8, fontSize: 13 }}>{error}</div>}
          {/* To */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 13 }}>To</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="text"
                placeholder="Enter recipient name, email, or Direct address"
                value={to}
                onChange={e => setTo(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', border: '1px solid #e3e7ed', borderRadius: 4, fontSize: 13, background: '#fafbfc' }}
              />
              <button
                style={{ background: '#f4f6fa', border: '1px solid #e3e7ed', borderRadius: 4, padding: 6, cursor: 'pointer' }}
                title="Address Book"
                onClick={() => setShowAddressBook(true)}
              >
                <span role="img" aria-label="address book">📒</span>
              </button>
            </div>
            {!showCcBcc && (
              <button type="button" style={{ color: '#1976d2', background: 'none', border: 'none', fontSize: 12, marginTop: 2, cursor: 'pointer' }} onClick={() => setShowCcBcc(true)}>
                + Add CC/BCC
              </button>
            )}
            {showCcBcc && (
              <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                <input
                  type="text"
                  placeholder="CC"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #e3e7ed', borderRadius: 4, fontSize: 13, background: '#fafbfc' }}
                />
                <input
                  type="text"
                  placeholder="BCC"
                  value={bcc}
                  onChange={e => setBcc(e.target.value)}
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #e3e7ed', borderRadius: 4, fontSize: 13, background: '#fafbfc' }}
                />
              </div>
            )}
          </div>
          
          {/* External Direct Message Indicator & HISP Selector */}
          {isExternalMessage && (
            <div style={{ marginBottom: 12, background: '#fff7e6', border: '1px solid #ffc107', borderRadius: 4, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FaGlobe style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#b45309' }}>External Direct Message</span>
              </div>
              <p style={{ fontSize: 12, color: '#78350f', margin: '0 0 8px 0' }}>
                This message will be sent externally via the Direct protocol to healthcare providers outside your organization.
              </p>
              {hispConnections.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontWeight: 500, fontSize: 12, color: '#78350f' }}>HISP Connection:</label>
                  <select
                    value={selectedHispConnection}
                    onChange={e => setSelectedHispConnection(e.target.value)}
                    style={{ flex: 1, padding: '6px 8px', border: '1px solid #fbbf24', borderRadius: 4, fontSize: 12, background: '#fffbeb' }}
                  >
                    <option value="">Select HISP connection...</option>
                    {hispConnections.map(conn => (
                      <option key={conn.id} value={conn.id}>
                        {conn.name} ({conn.type.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
              ) : loadingConnections ? (
                <span style={{ fontSize: 12, color: '#78350f' }}>Loading HISP connections...</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: 12 }}>
                  <FaInfoCircle />
                  <span>No HISP connections available. Contact your administrator.</span>
                </div>
              )}
            </div>
          )}
          {/* Subject */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 13 }}>Subject</label>
            <input
              type="text"
              placeholder="Enter message subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e3e7ed', borderRadius: 4, fontSize: 13, background: '#fafbfc' }}
            />
          </div>
          {/* Priority */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 13 }}>Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
              style={{ width: 120, padding: '8px 10px', border: '1px solid #e3e7ed', borderRadius: 4, fontSize: 13, background: '#fafbfc' }}
            >
              {priorities.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          {/* Message */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 13 }}>Message</label>
            <div style={{ border: '1px solid #e3e7ed', borderRadius: 4, background: '#fafbfc' }}>
              <div style={{ borderBottom: '1px solid #e3e7ed', padding: '4px 8px', display: 'flex', gap: 6 }}>
                <button type="button" style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer' }} title="Bold" onClick={() => handleFormat('bold')}><b>B</b></button>
                <button type="button" style={{ background: 'none', border: 'none', fontSize: 14, fontStyle: 'italic', cursor: 'pointer' }} title="Italic" onClick={() => handleFormat('italic')}><i>I</i></button>
                <button type="button" style={{ background: 'none', border: 'none', fontSize: 14, textDecoration: 'underline', cursor: 'pointer' }} title="Underline" onClick={() => handleFormat('underline')}><u>U</u></button>
                <button type="button" style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer' }} title="Bulleted List" onClick={() => handleFormat('insertUnorderedList')}>• List</button>
                <button type="button" style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer' }} title="Numbered List" onClick={() => handleFormat('insertOrderedList')}>1. List</button>
              </div>
              <div
                contentEditable
                suppressContentEditableWarning
                style={{ minHeight: 80, padding: '8px 10px', fontSize: 13, outline: 'none', background: 'transparent' }}
                onInput={e => setBody(e.currentTarget.innerHTML)}
                ref={contentEditableRef}
              />
            </div>
          </div>
          {/* Attachments */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: 13 }}>Attachments</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f4f6fa', border: '1px solid #e3e7ed', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
                <FaPaperclip /> Attach Files
                <input type="file" multiple style={{ display: 'none' }} onChange={handleAttach} />
              </label>
              {attachments.length > 0 && (
                <span style={{ fontSize: 12, color: '#1976d2' }}>{attachments.length} file(s) attached</span>
              )}
            </div>
          </div>
          {/* Secure Message Info */}
          <div style={{ background: '#eaf4ff', border: '1px solid #b6d4fe', borderRadius: 4, padding: '10px 12px', color: '#1976d2', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span role="img" aria-label="shield">🛡️</span>
            <div>
              <b>Secure Message</b><br />
              <span style={{ color: '#3b4a6b', fontSize: 12 }}>This message will be encrypted and transmitted securely in compliance with HIPAA regulations.</span>
            </div>
          </div>
          {/* Footer Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
            <div style={{ color: '#6b7280', fontSize: 13, display: 'flex', gap: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span role="img" aria-label="clock">🕒</span> Schedule Send</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span role="img" aria-label="eye">👁️</span> Preview</span>
            </div>
            <div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontWeight: 500, fontSize: 14, marginRight: 12, cursor: 'pointer' }}>Cancel</button>
              <button
                style={{ background: '#21ba45', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                onClick={handleSend}
                disabled={loading}
              >
                <FaPaperPlane style={{ marginRight: 6 }} />{loading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Address Book Modal */}
      {showAddressBook && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 4px 32px rgba(0,0,0,0.13)', minWidth: 500, maxWidth: '98vw', maxHeight: '90vh', overflow: 'auto', position: 'relative' }}>
            <button
              onClick={() => setShowAddressBook(false)}
              style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', zIndex: 10 }}
              title="Close"
            >
              ×
            </button>
            <AddressBook onSelectContact={handleSelectContact} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposeMessageModal; 